import { Box, Typography, Skeleton, Tooltip, IconButton } from "@mui/material";
// import cardBg from '../../../src/assets/images/background/opCardBg.png'
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import preScreenCompleteIcon from "../../assets/images/icons/pre_screen_complete.svg";
import patientFeasibilityIcon from "../../assets/images/icons/patient_feasibility_icon.svg";
import slotSchedulingIcon from "../../assets/images/icons/slot_scheduling_icon.svg";
import lsmCollectionIcon from "../../assets/images/icons/lsm_collection_icon.svg";
import productManufacturingIcon from "../../assets/images/icons/product_manufacturing_icon.svg";
import finishedProductDispatchIcon from "../../assets/images/icons/finished_product_dispatch_icon.svg";
import infusionIcon from "../../assets/images/icons/infusion_icon.svg";
import vectorTileBg from "../../assets/images/icons/Vector.png";
import { getUserHasAccess } from "../../utils/authorities";
import { cardRouteMap } from "../../bmr/utils/common/utils";

const tileLabelMap = {
  onboarding_pre_screening: "Pre-screening Completed",
  patient_feasibility: "Feasibility Done",
  slot_scheduling: "Slots Confirmed",
  lsm_dispatched: "LSM Dispatched",
  lsm_accepted: "LSM Accepted",
  product_manufacturing: "Batch Manufactured",
  finished_product_dispatch: "Final Product dispatch",
  infusion: "Patient/Subject Infused",
};

const tileIconPathMap = {
  onboarding_pre_screening: preScreenCompleteIcon,
  patient_feasibility: patientFeasibilityIcon,
  slot_scheduling: slotSchedulingIcon,
  lsm_dispatched: lsmCollectionIcon,
  lsm_accepted: lsmCollectionIcon,
  product_manufacturing: productManufacturingIcon,
  finished_product_dispatch: finishedProductDispatchIcon,
  infusion: infusionIcon,
};


const PatientDetailsStats = ({ dashboardData, loading: parentLoading }) => {
  const navigate = useNavigate();
  const LoginData = useSelector((state) => state.loginData);
  const userAuthority = LoginData?.auth || [];
  const bmr_permissions = LoginData?.bmr_permissions || [];

  const checkAccess = (key) => {
    const config = cardRouteMap[key];
    if (!config) return false;

    if (config.permissionType === "bmr_permission") {
      return config.permissions.some((perm) => bmr_permissions.includes(perm));
    }

    if (config.permissionType === "authority") {
      return getUserHasAccess(userAuthority, config.module, config.permission_id);
    }

    return false;
  };

  const handleCardClick = (key) => {
    if (!checkAccess(key)) return;
    const config = cardRouteMap[key];
    if (config && config.route) {
      navigate(config.route, {
        state: {
          prescreen_dashboard: key === "onboarding_pre_screening" ? 1 : 0,
          feasibility_dashboard: key === "patient_feasibility" ? 1 : 0,
          lsmDispatched_dashboard: key === "lsm_dispatched" ? 1 : 0,
          lsmAccepted_dashboard: key === "lsm_accepted" ? 1 : 0,
          productManufacturing_dashboard: key === "product_manufacturing" ? 1 : 0,
          finishedProductDispatch_dashboard: key === "finished_product_dispatch" ? 1 : 0,
          infusion_dashboard: key === "infusion" ? 1 : 0,
        },
      });
    }
  };
  //@info getting the data from the parent component.
  const excludedKeys = [
    "recent_activity",
    "total_phase",
    "total_patient",
    "phase_statistics",
  ];

  const recentActivityCRData = useMemo(() => {
    if (!dashboardData) return [];

    return Object.entries(dashboardData)
      .filter(([key, value]) =>
        !excludedKeys.includes(key) && typeof value === "number"
      )
      .map(([key, count]) => ({
        key,
        label: key
          .replaceAll("_", " ")
          .replace(/^./, (char) => char.toUpperCase()),
        value: count,
      }));
  }, [dashboardData]);

  const fullCapsWords = ["lsm", "bmr", "cdm"];

  const formatLabel = (str) => {
    if (!str) return "";

    return str
      .split(" ")
      .map(word => {
        if (fullCapsWords.includes(word.toLowerCase())) {
          return word.toUpperCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");
  };

  const getTileLabel = (key, fallbackLabel) => {
    return tileLabelMap[key] || formatLabel(fallbackLabel);
  };

  const getTileIconPath = (key) => {
    return tileIconPathMap[key] || "";
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        padding: "16px 24px",
        boxShadow: "0px 2px 6px rgba(0,0,0,0.04)",
        mt: 2,
      }}
    >
      <Typography variant="h6" fontWeight={600} mb={2}>
        Patient/Subject Journey Overview
      </Typography>

      {/* Cards Row */}
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          overflowX: "auto",
          paddingBottom: "8px",
          "&::-webkit-scrollbar": { height: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#E0E0E0",
            borderRadius: "8px",
          },
        }}
      >
        {parentLoading
          ? Array.from({ length: recentActivityCRData?.length || 8 }).map((_, index) => (
            <Box
              key={index}
              sx={{
                minWidth: "156px",
                padding: "8px",
                borderRadius: "12px",
                border: "1px solid #E6EEF8",
                backgroundColor: "#F9FBFF",
                backgroundImage: `url(${vectorTileBg})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right top",
                backgroundSize: "95px auto",
                flexShrink: 0,
              }}
            >
              <Skeleton variant="circular" width={40} height={40} />
              <Skeleton variant="text" width={60} height={42} />
              <Skeleton variant="text" width="80%" height={20} />
            </Box>
          ))
          : recentActivityCRData?.map((item, index) => (
            <Tooltip key={index} title={checkAccess(item.key) ? "" : "You don't have access to view this section"} arrow>
              <Box
                onClick={() => handleCardClick(item.key)}
                sx={{
                  minWidth: "156px",
                  padding: "8px",
                  borderRadius: "12px",
                  border: "1px solid #E6EEF8",
                  backgroundColor: "#FFFFFF",
                  backgroundImage: `url(${vectorTileBg})`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right top",
                  backgroundSize: "95px auto",
                  flexShrink: 0,
                  cursor: checkAccess(item.key) ? "pointer" : "default",
                }}
              >
                {/* adding icon with icon button here */}
                <Box sx={{ ml: -0.1, mb: 1 }}>
                  <IconButton
                    size="small"
                    sx={{
                      borderRadius: "8px",
                      width: 30,
                      height: 30,
                      p: 0,
                      justifyContent: "flex-start",
                    }}
                  >
                    {getTileIconPath(item.key) ? (
                      <Box
                        component="img"
                        src={getTileIconPath(item.key)}
                        alt={getTileLabel(item.key, item.label)}
                        sx={{ width: 30, height: 30, objectFit: "contain", padding: 0 }}
                      />
                    ) : (
                      <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#3571FF" }} />
                    )}
                  </IconButton>
                </Box>

                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="#222222"
                  sx={{ lineHeight: 1, mb: 1 }}
                >
                  {item.value}
                </Typography>

                <Typography variant="body2" color="#222222" fontWeight={500} fontSize={12} lineHeight={1.05}>
                  {getTileLabel(item.key, item.label)}
                </Typography>
                <Box
                  sx={{
                    mt: 0.8,
                    width: "100%",
                    borderBottom: "1px solid #E6EEF8",
                  }}
                />
              </Box>
            </Tooltip>
          ))}
      </Box>
    </Box>
  );
};

export default PatientDetailsStats;
